// Canonical engine wire emit for media bytes (waGetMessageMediaTool).
//
// Wrapped shape — both engines (chrome extension + baileys pod) emit
// `{mediaFile: {...}, mediaUnavailable: ...}` at the IPC boundary. The
// camelCase field names match the WAWeb / @whiskeysockets/baileys
// ergonomics on the engine side; the backend `toPublicMedia` mapper
// (services/api/v1/chats/chats.mapper.ts) projects this to the
// snake_case `PublicMediaSchema` exposed by the v1 REST surface.
//
// Reference emits:
//   - baileys pod  → gateway/baileys/handlers/media-download.ts:144-151
//   - chrome ext   → whatsapp-scheduler/.../page/services/PageMessageService.ts
//                    (uploads File, returns {mediaFile:{mediaUrl,...}})
//
// `originalQuality: false` indicates the bytes are a WA-generated
// preview rather than the sender-uploaded original (#113 own-sent
// newsletter media). `mediaUnavailable` is set when the bytes
// couldn't be retrieved (the WA-tracked mediaStage maps to the
// reason). The preview-mode response shape lives in
// WaMessageMediaPreview (extension-side) and is distinct from this
// full-mode media payload.

import { z } from 'zod';

/** Engine-emit states for the bytes when they couldn't be retrieved. */
export const WaMediaUnavailableSchema = z.enum([
  'expired',
  'fetching',
  'awaiting_sender',
  'error',
  'no_media',
]);
export type WaMediaUnavailable = z.infer<typeof WaMediaUnavailableSchema>;

/** Inner `mediaFile` shape — engine emits a wrapper sub-object with
 *  the file payload (data or upload URL) + WAWeb-style camelCase
 *  metadata. */
export const BtSlimMediaFileSchema = z.object({
  /** Inline base64 bytes — `agent-vision` mode ONLY (the downsized JPEG).
   *  `full` never carries inline bytes. */
  data: z.string().optional(),
  /** `full`: the signed URL from the backend upload (`upload-mcp-media`),
   *  the only delivery form. */
  mediaUrl: z.string().optional(),
  /** MIME type ("image/jpeg", "video/mp4", …). */
  fileType: z.string().optional(),
  fileName: z.string().optional(),
  /** Byte size of the file. Always emitted on `full` by a contract engine.
   *  @5.0b required on full. */
  fileSize: z.number().int().optional(),
  /** Upload-URL expiry ISO 8601 timestamp. */
  expiresAt: z.string().optional(),
  /** False when the bytes are a WA-generated preview rather than the
   *  sender-uploaded original (#113 own-sent newsletter media). */
  originalQuality: z.boolean().optional(),
}).passthrough();
export type BtSlimMediaFile = z.infer<typeof BtSlimMediaFileSchema>;

export const BtSlimMediaSchema = z.object({
  mediaFile: BtSlimMediaFileSchema.nullable().optional(),
  /** Set on EVERY failure, in every mode. Pod mapping: 403/404/410 →
   *  `expired`; a pending re-upload request → `awaiting_sender`; a text
   *  message → `no_media`. */
  mediaUnavailable: WaMediaUnavailableSchema.nullable().optional(),
}).passthrough();
export type BtSlimMedia = z.infer<typeof BtSlimMediaSchema>;
